import React, { useEffect, useState, useMemo, useContext } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AuthContext } from '../../contexts/AuthContext';
import siteService from '../../services/siteService';
import participantService from '../../services/participantService';
import { Building, Globe, User, Mail, UserPlus, Edit2, ChevronLeft, Search, Filter, Power, PowerOff, Calendar, Loader, Users, Bell, Activity, CheckCircle2, AlertTriangle } from 'lucide-react';
import PageHeader from '../Shared/PageHeader';
import ContextualNotificationToggle from '../Notifications/ContextualNotificationToggle';
import { toast } from 'react-hot-toast';
import hasUserRole from '../../utils/UserUtil'
import permissionsService from '../../services/permissionsService'
import AddUserModal from '../User/AddUserModal'
import BulkCreateParticipantsModal from '../Participant/BulkCreateParticipantsModal'
import { Tab } from '@headlessui/react';
import {
  PARTICIPANTS_PER_SITE_PAGE,
  paginateSiteParticipants,
} from '../../utils/siteParticipantPagination';

const SITE_TABS = [
  { id: 'overview', label: 'Overview', icon: Activity },
  { id: 'notifications', label: 'Notifications', icon: Bell },
];

// FECOA-1609: small badge for a participant's survey-window generation status.
const renderGenerationBadge = (gen) => {
  if (!gen || gen.status === 'complete') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-success-600 dark:text-success-400">
        <CheckCircle2 size={14} /> Ready
      </span>
    );
  }
  if (gen.status === 'failed') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-danger-500" title={gen.error || 'Generation failed'}>
        <AlertTriangle size={14} /> Failed
      </span>
    );
  }
  // pending / in_progress
  return (
    <span className="inline-flex items-center gap-1 text-xs text-warning-600 dark:text-warning-400">
      <Loader size={14} className="animate-spin" /> Building…
    </span>
  );
};

const SiteDetailsPage = () => {
  const { studyID, siteID } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useContext(AuthContext);
  const searchParams = new URLSearchParams(location.search);
  const [participants, setParticipants] = useState([]);
  const [filteredParticipants, setFilteredParticipants] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'participant_id', direction: 'ascending' });
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: []
  });
  const [siteData, setSiteData] = useState({
    site_name: '',
    institution: '',
    country: '',
    contact_first_name: '',
    contact_last_name: '',
    contact_email: ''
  });
  const [permissions, setPermissions] = useState({
    canChangeSite: false,
    canAddParticipant: false,
    canActivateParticipant: false,
    canDeactivateParticipant: false,
    canAddUser: false,
    canBulkCreateParticipants: false,
  });

  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [isBulkCreateOpen, setIsBulkCreateOpen] = useState(false);
  // FECOA-1609: window-generation status keyed by participant UUID.
  const [genStatusMap, setGenStatusMap] = useState({});
  const [activeTab, setActiveTab] = useState(0);

  const getActiveTabFromURL = () => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    if (tabParam) {
      const t = SITE_TABS.findIndex(tab => tab.id === tabParam);
      if (t >= 0) return t;
    }
    if (location.hash) {
      const id = location.hash.substring(1);
      const t = SITE_TABS.findIndex(tab => tab.id === id);
      if (t >= 0) return t;
    }
    return 0;
  };

  useEffect(() => {
    const newTab = getActiveTabFromURL();
    if (newTab !== activeTab) {
      setActiveTab(newTab);
    }
  }, [location.search, location.hash]);

  const updateActiveTab = (tab) => {
    const tabId = SITE_TABS[tab].id;
    if (tabId === 'overview')
      navigate(`/studies/${studyID}/sites/${siteID}`, { replace: true });
    else
      navigate(`/studies/${studyID}/sites/${siteID}#${tabId}`, { replace: true });
    setActiveTab(tab);
  };

  useEffect(() => {
    const fetchSiteDetails = async () => {
      try {
        const data = await siteService.getSiteDetailsForStudy(studyID, siteID);
        setSiteData(data);
        setError('');
      } catch (error) {
        console.error('Error fetching site details:', error);
        setError('An error occurred while fetching the site details.');
      }
    };

    const fetchParticipants = async () => {
      try {
        const data = await participantService.getParticipantsForSite(studyID, siteID);
        console.log('Fetched participants:', data);
        const participantsArray = Array.isArray(data.participants) ? data.participants : [];
        console.log('Participants array:', participantsArray);
        setParticipants(participantsArray);
        setFilteredParticipants(participantsArray);
        setError('');
      } catch (error) {
        console.error('Error fetching participants:', error);
        setError('An error occurred while fetching the participants.');
      } finally {
        setLoading(false);
      }
    };

    if (studyID && siteID) {
      setLoading(true);
      fetchSiteDetails();
      fetchParticipants();
    }
  }, [studyID, siteID]);

  // FECOA-1609: poll window-generation status for this study and keep polling
  // while any participant is still generating; stop once all are terminal.
  useEffect(() => {
    if (!studyID) return;
    let cancelled = false;
    let timer = null;

    const poll = async () => {
      try {
        const data = await participantService.getGenerationStatus({ studyID });
        if (cancelled) return;
        const map = {};
        (data.participants || []).forEach((p) => { map[p.participant_id] = p; });
        setGenStatusMap(map);
        const stillBuilding = (data.participants || []).some(
          (p) => p.status === 'pending' || p.status === 'in_progress'
        );
        if (stillBuilding) timer = setTimeout(poll, 5000);
      } catch {
        // Non-fatal; the column just won't show a badge this cycle.
      }
    };

    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [studyID, participants.length]);

  // Check permissions
  useEffect(() => {
    const checkPermissions = async () => {
      if (user) {
        try {
          const [hasChangeSite, hasAddParticipant, hasActivateParticipant, hasDeactivateParticipant, hasAddUser, hasBulkCreate] = await Promise.all([
            permissionsService.hasPermission(permissionsService.PERMISSIONS.CHANGE_SITE),
            permissionsService.hasPermission(permissionsService.PERMISSIONS.ADD_PARTICIPANT),
            permissionsService.hasPermission(permissionsService.PERMISSIONS.ACTIVATE_PARTICIPANT),
            permissionsService.hasPermission(permissionsService.PERMISSIONS.DEACTIVATE_PARTICIPANT),
            permissionsService.hasPermission(permissionsService.PERMISSIONS.ADD_USER),
            permissionsService.hasPermission(permissionsService.PERMISSIONS.BULK_CREATE_PARTICIPANTS),
          ]);

          setPermissions({
            canChangeSite: hasChangeSite,
            canAddParticipant: hasAddParticipant,
            canActivateParticipant: hasActivateParticipant,
            canDeactivateParticipant: hasDeactivateParticipant,
            canAddUser: hasAddUser,
            canBulkCreateParticipants: hasBulkCreate,
          });
        } catch (error) {
          console.error('Error checking permissions:', error);
          setPermissions({
            canChangeSite: false,
            canAddParticipant: false,
            canActivateParticipant: false,
            canDeactivateParticipant: false,
            canAddUser: false,
            canBulkCreateParticipants: false,
          });
        }
      }
    };
    
    checkPermissions();
  }, [user]);

  useEffect(() => {
    const filtered = participants.filter(participant => {
      const searchTermLower = searchTerm.toLowerCase();
      const matchesSearch = (
        (participant.participant_id && participant.participant_id.toLowerCase().includes(searchTermLower)) ||
        (participant.participant_number && participant.participant_number.toString().toLowerCase().includes(searchTermLower)) ||
        (participant.is_active !== undefined && participant.is_active.toString().toLowerCase().includes(searchTermLower))
      );
      
      const matchesFilters = 
        (filters.status.length === 0 || 
          (participant.is_active && filters.status.includes('active')) ||
          (!participant.is_active && filters.status.includes('inactive')));

      return matchesSearch && matchesFilters;
    });
    setFilteredParticipants(filtered);
  }, [searchTerm, participants, filters]);

  const extractParticipantNumber = (participantId) => {
    const match = participantId.match(/(\d+)$/);
    return match ? parseInt(match[1], 10) : 0;
  };

  const sortedParticipants = useMemo(() => {
    let sortableParticipants = [...filteredParticipants];
    if (sortConfig.key) {
      sortableParticipants.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (sortConfig.key === 'id') {
          aValue = extractParticipantNumber(a.id);
          bValue = extractParticipantNumber(b.id);
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableParticipants;
  }, [filteredParticipants, sortConfig]);

  const paginatedParticipants = useMemo(
    () => paginateSiteParticipants(sortedParticipants, currentPage),
    [sortedParticipants, currentPage]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters, sortConfig]);

  const handleActivateDeactivate = (participant) => {
    if (participant.is_active) {
      navigate(`/studies/${studyID}/sites/${siteID}/participant/${participant.id}/deactivate`);
    } else {
      navigate(`/studies/${studyID}/sites/${siteID}/participant/${participant.id}/activate`);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' }).toUpperCase();
    const year = date.getFullYear();
    return `${day}${month}${year}`;
  };

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const toggleFilter = (filterType, value) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      [filterType]: prevFilters[filterType].includes(value)
        ? prevFilters[filterType].filter(item => item !== value)
        : [...prevFilters[filterType], value]
    }));
  };

  const renderFilterOptions = () => {
    const statusOptions = ['active', 'inactive'];

    return (
      <div className="bg-white dark:bg-gray-800 p-4 rounded-md shadow-md dark:shadow-gray-900/20 mb-4">
        <div className="mb-4">
          <h3 className="font-semibold mb-2 capitalize text-gray-900 dark:text-gray-100">Status</h3>
          <div className="flex flex-wrap gap-2">
            {statusOptions.map(option => (
              <button
                key={option}
                onClick={() => toggleFilter('status', option)}
                className={`px-3 py-1 rounded-full text-sm ${
                  filters.status.includes(option)
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                {option.charAt(0).toUpperCase() + option.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader className="w-16 h-16 text-primary-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-md dark:shadow-gray-900/20">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Error</h1>
          <p className="text-gray-700 dark:text-gray-200">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 transition-colors duration-200"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!participants?.length) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <Link
            to={`/studies/${studyID}#sites`}
            className="mb-4 flex items-center text-primary-600 hover:text-primary-700 transition-colors duration-200"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            <span>Back to Study</span>
          </Link>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md dark:shadow-gray-900/20 overflow-hidden">
            <div className="px-6 py-4 bg-gradient-to-r from-primary-600 to-primary-700">
              <h2 className="text-xl font-semibold text-white">Site Details</h2>
            </div>
            <div className="p-12">
              <div className="flex flex-col items-center justify-center max-w-md mx-auto">
                <div className="bg-gray-50 dark:bg-gray-900 rounded-full p-6 mb-6">
                  <Users className="w-12 h-12 text-gray-400 dark:text-gray-500" />
                </div>
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-3">No Participants Found</h2>
                {permissions.canAddParticipant ? (
                  <div className="text-center">
                  <p className="text-gray-500 dark:text-gray-400 text-center mb-8">
                    This site currently has no participants enrolled. Add your first participant to get started with data collection and management.
                  </p>
                  <Link
                    to={`/studies/${studyID}/sites/${siteID}/participants/add-participant`}
                    className="inline-flex items-center px-5 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200 font-medium"
                  >
                    <UserPlus className="w-5 h-5 mr-2" />
                    Add First Participant
                  </Link>
                  </div>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 text-center mb-8">
                    This site currently has no participants enrolled.
                  </p>
                 )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <Link
            to={`/studies/${studyID}#sites`}
            className="mb-4 flex items-center text-primary-600 hover:text-primary-700 transition-colors duration-200"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            <span>Back to Study</span>
          </Link>
          <PageHeader
            icon={Building}
            title={siteData.site_name || 'Site'}
            subtitle="View site details, participants, and notifications"
          >
            {permissions.canChangeSite && (
              <Link
                to={`/studies/${studyID}/sites/${siteID}/edit`}
                className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-all duration-200 shadow-sm dark:shadow-gray-900/20"
              >
                <Edit2 className="w-4 h-4 mr-2" />
                Edit Site
              </Link>
            )}
            {permissions.canAddUser && (
              <button
                onClick={() => setIsAddUserModalOpen(true)}
                className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-all duration-200 shadow-sm dark:shadow-gray-900/20"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Add User
              </button>
            )}
          </PageHeader>
        </motion.div>

        <div className="bg-white dark:bg-gray-800 shadow-lg dark:shadow-gray-900/20 rounded-lg overflow-hidden">
          <div className="px-6 py-4 bg-primary-600 dark:bg-primary-700">
            <h2 className="text-xl font-semibold text-white">Site Details</h2>
          </div>
          <div className="p-6">
            <Tab.Group selectedIndex={activeTab} onChange={updateActiveTab}>
              <Tab.List className="flex space-x-4 border-b border-gray-200 dark:border-gray-700 mb-6">
                {SITE_TABS.map((tab) => (
                  <Tab
                    key={tab.id}
                    className={({ selected }) =>
                      `px-4 py-2 text-sm font-medium leading-5 focus:outline-none ${
                        selected ? 'text-primary-700 border-b-2 border-primary-600' : 'text-gray-500 dark:text-gray-400 hover:text-primary-600'
                      }`
                    }
                  >
                    <div className="flex items-center">
                      <tab.icon size={18} className="mr-2" />
                      {tab.label}
                    </div>
                  </Tab>
                ))}
              </Tab.List>
              <Tab.Panels>
                {/* Overview Tab */}
                <Tab.Panel>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                      <div className="flex items-start">
                        <Building className="text-primary-600 mr-3 mt-1" size={20} />
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">Institution</p>
                          <p className="text-lg font-semibold">{siteData.institution}</p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <Globe className="text-primary-600 mr-3 mt-1" size={20} />
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">Country</p>
                          <p className="text-lg font-semibold">{siteData.country}</p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <User className="text-primary-600 mr-3 mt-1" size={20} />
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">Contact</p>
                          <p className="text-lg font-semibold">{`${siteData.contact_first_name} ${siteData.contact_last_name}`}</p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <Mail className="text-primary-600 mr-3 mt-1" size={20} />
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">Contact Email</p>
                          <p className="text-lg font-semibold">{siteData.contact_email}</p>
                        </div>
                      </div>
                    </div>

                    <div id="participants-section" className="mt-8">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Participants</h3>
                      <div className="flex justify-between items-center mb-6">
                        <div className="relative">
                          <input
                            type="text"
                            placeholder="Search participants..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-primary-500 focus:border-primary-500 focus:outline-none focus:ring-2"
                          />
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={20} />
                        </div>
                        <div className="flex space-x-4">
                          <button 
                            onClick={() => setShowFilters(!showFilters)}
                            className={`flex items-center px-4 py-2 border rounded-md text-white ${showFilters ? 'bg-primary-600 border-primary-600' : 'bg-gray-400 border-gray-400 dark:bg-gray-600 dark:border-gray-600'
                              } hover:bg-opacity-90`}
                          >
                            <Filter size={20} className="mr-2" />
                            {showFilters ? 'Hide Filters' : 'Show Filters'}
                          </button>
                          {permissions.canAddParticipant && (
                          <Link
                            to={`/studies/${studyID}/sites/${siteID}/participants/add-participant`}
                            className="bg-primary-600 text-white px-4 py-2 rounded-md text-sm flex items-center hover:bg-primary-700 transition duration-300"
                          >
                            <UserPlus size={18} className="mr-2" />
                            Add Participant
                          </Link>
                          )}
                          {permissions.canBulkCreateParticipants && (
                          <button
                            onClick={() => setIsBulkCreateOpen(true)}
                            className="bg-primary-600 text-white px-4 py-2 rounded-md text-sm flex items-center hover:bg-primary-700 transition duration-300"
                          >
                            <Users size={18} className="mr-2" />
                            Bulk Add
                          </button>
                          )}
                        </div>
                      </div>

                      {showFilters && renderFilterOptions()}

                      <div className="overflow-x-auto">
                        {sortedParticipants.length === 0 ? (
                          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md dark:shadow-gray-900/20 overflow-hidden">
                            <div className="px-6 py-4 bg-gradient-to-r from-primary-600 to-primary-700">
                              <h2 className="text-xl font-semibold text-white">Search Results</h2>
                            </div>
                            <div className="flex flex-col items-center justify-center py-12">
                              <div className="bg-gray-50 dark:bg-gray-900 rounded-full p-4 mb-4">
                                <Search className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                              </div>
                              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No Matching Participants</h3>
                              <p className="text-gray-500 dark:text-gray-400 text-center max-w-sm">
                                No participants match your current search criteria. Try adjusting your filters or search terms.
                              </p>
                            </div>
                          </div>
                        ) : (
                          <>
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                              <thead className="bg-gray-50 dark:bg-gray-900">
                                <tr>
                                  <th
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer"
                                    onClick={() => requestSort('participant_id')}
                                  >
                                    Participant ID
                                    {sortConfig.key === 'participant_id' && (
                                      <span className="ml-1">
                                        {sortConfig.direction === 'ascending' ? '▲' : '▼'}
                                      </span>
                                    )}
                                  </th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Participant Number</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Setup</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {paginatedParticipants.items.map(participant => (
                                  <tr key={participant.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition duration-150">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <Link
                                        to={`/studies/${studyID}/sites/${siteID}/participant/${participant.id || ''}`}
                                        className="text-primary-600 hover:text-primary-700 font-medium"
                                      >
                                        {participant.participant_id || 'N/A'}
                                      </Link>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <span className="text-gray-700 dark:text-gray-200">
                                        {participant.participant_id ? participant.participant_id.split('-')[1] : 'N/A'}
                                      </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${participant.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                        {participant.is_active ? 'Active' : 'Inactive'}
                                      </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      {renderGenerationBadge(genStatusMap[participant.id])}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                    {((participant.is_active && permissions.canDeactivateParticipant) || (!participant.is_active && permissions.canActivateParticipant)) && (
                                      <button
                                        onClick={() => handleActivateDeactivate(participant)}
                                        className={`p-1 rounded-full ${participant.is_active
                                          ? 'text-red-600 hover:bg-red-100'
                                          : 'text-green-600 hover:bg-green-100'
                                        } transition-colors duration-200`}
                                        title={participant.is_active ? 'Deactivate' : 'Activate'}
                                      >
                                        {participant.is_active ? <PowerOff size={18} /> : <Power size={18} />}
                                      </button>
                                    )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>

                            {sortedParticipants.length > PARTICIPANTS_PER_SITE_PAGE && (
                              <div className="mt-4 flex flex-col gap-3 border-t border-gray-200 dark:border-gray-700 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
                                <p className="text-sm text-gray-700 dark:text-gray-300">
                                  Showing <span className="font-medium">{paginatedParticipants.startItem}</span> to{' '}
                                  <span className="font-medium">{paginatedParticipants.endItem}</span> of{' '}
                                  <span className="font-medium">{paginatedParticipants.totalItems}</span> participants
                                </p>
                                <nav className="relative z-0 inline-flex rounded-md shadow-sm dark:shadow-gray-900/20 -space-x-px" aria-label="Participant pagination">
                                  <button
                                    type="button"
                                    onClick={() => setCurrentPage(page => Math.max(1, page - 1))}
                                    disabled={paginatedParticipants.currentPage === 1}
                                    className="relative inline-flex items-center px-3 py-2 rounded-l-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                                  >
                                    Previous
                                  </button>
                                  {Array.from({ length: paginatedParticipants.totalPages }).map((_, index) => {
                                    const pageNumber = index + 1;
                                    const isActivePage = paginatedParticipants.currentPage === pageNumber;

                                    return (
                                      <button
                                        key={pageNumber}
                                        type="button"
                                        onClick={() => setCurrentPage(pageNumber)}
                                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                          isActivePage
                                            ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                                            : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'
                                        }`}
                                        aria-current={isActivePage ? 'page' : undefined}
                                      >
                                        {pageNumber}
                                      </button>
                                    );
                                  })}
                                  <button
                                    type="button"
                                    onClick={() => setCurrentPage(page => Math.min(paginatedParticipants.totalPages, page + 1))}
                                    disabled={paginatedParticipants.currentPage === paginatedParticipants.totalPages}
                                    className="relative inline-flex items-center px-3 py-2 rounded-r-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                                  >
                                    Next
                                  </button>
                                </nav>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </motion.div>
                </Tab.Panel>

                {/* Notifications Tab */}
                <Tab.Panel>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                  >
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Site Notifications</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                          Configure which notifications you want to receive for events at this site.
                        </p>
                      </div>

                      <div className="space-y-4">
                        <ContextualNotificationToggle
                          eventKey="survey.missed"
                          label="Missed Surveys"
                          description="Get notified when participants miss survey deadlines"
                          studyId={studyID}
                          siteId={siteID}
                        />
                        
                        <ContextualNotificationToggle
                          eventKey="participant.created"
                          label="New Participants"
                          description="Know when new participants are enrolled"
                          studyId={studyID}
                          siteId={siteID}
                        />
                        
                        <ContextualNotificationToggle
                          eventKey="participant.activated"
                          label="Participant Activations"
                          description="Be informed when participants are activated"
                          studyId={studyID}
                          siteId={siteID}
                        />

                        <ContextualNotificationToggle
                          eventKey="participant.deactivated"
                          label="Participant Deactivations"
                          description="Get alerts when participants are deactivated"
                          studyId={studyID}
                          siteId={siteID}
                        />
                      </div>

                      <div className="pt-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Notifications specific to <strong>{siteData.site_name}</strong>
                        </p>
                        <Link
                          to="/profile?tab=notifications"
                          className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                        >
                          Manage all notifications →
                        </Link>
                      </div>
                    </div>
                  </motion.div>
                </Tab.Panel>
              </Tab.Panels>
            </Tab.Group>
          </div>
        </div>

        {/* Add User Modal */}
        <AddUserModal
          isOpen={isAddUserModalOpen}
          onClose={() => setIsAddUserModalOpen(false)}
          onSuccess={() => {
            setIsAddUserModalOpen(false);
          }}
          sponsorId={siteData?.sponsor?.id}
          studyId={studyID}
          siteId={siteData?.id}
        />
        {/* Bulk Create Participants Modal */}
        <BulkCreateParticipantsModal
          isOpen={isBulkCreateOpen}
          onClose={() => setIsBulkCreateOpen(false)}
          onSuccess={() => { setIsBulkCreateOpen(false); window.location.reload(); }}
          studyId={studyID}
          siteId={siteID}
          sites={[siteData]}
          studyName=""
          sponsorCode=""
        />
      </div>
    </div>
  );
};

export default SiteDetailsPage;